/**
 * Tooltip Component
 * Canon: SUPABANK CANON v1.0.8
 * 
 * Professional tooltip for truncated text, IDs, and email addresses
 * Stripe-quality micro-interactions
 * 
 * CRITICAL FIX 2025-10-31: Overridden TooltipTrigger to NEVER use asChild
 * This prevents React.Children.only errors throughout the application
 */

import { cn } from '@/lib/utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;

// NUCLEAR FIX: Override TooltipTrigger to NEVER use asChild
// This prevents React.Children.only validation errors completely
export const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ asChild, children, ...props }, ref) => {
  // ALWAYS use asChild=false - prevents React.Children.only() errors
  // Trade-off: Additional button wrapper, but 100% crash-proof
  return (
    <TooltipPrimitive.Trigger ref={ref} asChild={false} {...props}>
      {children}
    </TooltipPrimitive.Trigger>
  );
});
TooltipTrigger.displayName = 'TooltipTrigger';

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-lg border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        'max-w-xs break-words',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
  className?: string;
  disabled?: boolean;
}

export function TooltipWrapper({
  content,
  children,
  side = 'top',
  delayDuration = 300,
  className,
  disabled = false,
}: TooltipProps) {
  if (disabled) {
    return children as React.ReactElement;
  }

  // Ensure children is always wrapped in a valid single element
  const validChild = React.isValidElement(children) ? children : <span>{children}</span>;

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger>
          {validChild}
        </TooltipTrigger>
        <TooltipContent side={side} className={className}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

export function TruncatedText({
  children,
  maxLength = 30,
  className,
}: {
  children: string;
  maxLength?: number;
  className?: string;
}) {
  const text = String(children || '');
  const isTruncated = text.length > maxLength;
  const displayText = isTruncated ? `${text.slice(0, maxLength)}...` : text;

  if (!isTruncated) {
    return <span className={className}>{text}</span>;
  }

  return (
    <TooltipWrapper content={text}>
      <button
        type="button"
        className={cn('cursor-help bg-transparent border-0 p-0 font-inherit text-inherit', className)}
      >
        {displayText}
      </button>
    </TooltipWrapper>
  );
}

export function CopyableText({
  value,
  children,
  className,
}: {
  value: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <TooltipWrapper
      content={copied ? 'Copied!' : `Click to copy: ${value}`}
      side="top"
    >
      <button
        type="button"
        className={cn(
          'cursor-pointer hover:underline inline-block',
          'transition-colors duration-120',
          copied && 'text-success',
          'bg-transparent border-0 p-0 font-inherit text-inherit',
          className
        )}
        onClick={handleCopy}
      >
        {children || value}
      </button>
    </TooltipWrapper>
  );
}

export default TooltipWrapper;
export { TooltipWrapper as Tooltip };
